import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faGraduationCap, faChartLine, faUpload, faSearch,
  faClipboardCheck, faCog, faSignOutAlt, faChartBar
} from "@fortawesome/free-solid-svg-icons";
import "./Header.css";
import bitsLogo from "../assets/bits_logo.png";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [error, setError] = useState(null);

  const [userData, setUserData] = useState({});
  const roles = Array.isArray(userData?.roles) ? userData.roles : [];

  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      const response = await axios.get(`/qpra/logout`, { withCredentials: true });
      if (response.data?.success && response.data?.redirect_url) {
        window.location.href = response.data.redirect_url;
      } else {
        console.warn('Logout API returned unexpected response:', response.data);
      }
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`/qpra/get-saml-data`, {
          method: "GET",
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        const data = await res.json();
        setUserData(data || {});
        setError(null);
      } catch (err) {
        console.error("Error fetching SAML data:", err);
        setUserData({});
        setError(err.message || "Unknown error");
      } finally {
        setAuthLoading(false);
      }
    })();
  }, []);

  // ---- Role helpers ----
  const hasAnyRole = (allowed = []) => allowed.some(r => roles.includes(r));
  const canShow = (allowed) => !authLoading && hasAnyRole(allowed);

  // Granular ACL per nav item (matches App routes)
  const ACL = {
    dataUpload: ['qprs_super_admin','qprs_admin','qprs_data_operator'],
    reviewerAssigner: ['qprs_super_admin','qprs_admin','qprs_hog'],
    review: ['qprs_super_admin','qprs_admin','qprs_reviewer'],
    upload: ['qprs_super_admin','qprs_admin','qprs_data_operator'],
    analyze: ['qprs_super_admin','qprs_admin','qprs_reviewer'],
    rubric: ['qprs_super_admin','qprs_admin'],
    analytics: ['qprs_super_admin','qprs_admin','qprs_leadership'],
  };

  return (
    <header className={`header ${isScrolled ? "scrolled" : ""}`}>
      <div className="container-header">
        <div className="header-content">
          {/* Logo */}
          <Link to="/" className="logo">
            <img src={bitsLogo} alt="BITS Logo" className="bits-logo-img" />
            <span className="logo-subtitle">Question Review System</span>
          </Link>

          <nav className="nav-desktop">
            {/* Data Upload */}
            {canShow(ACL.dataUpload) && (
              <Link
                to="/data_operator_upload"
                className={`nav-link ${isActive('/data_operator_upload') ? 'active' : ''}`}
              >
                <FontAwesomeIcon icon={faUpload} />
                CSV Upload
              </Link>
            )}

            {/* Reviewer Assigner */}
            {canShow(ACL.reviewerAssigner) && (
              <Link
                to="/assigner_page"
                className={`nav-link ${isActive('/assigner_page') ? 'active' : ''}`}
              >
                <FontAwesomeIcon icon={faChartLine} />
                Reviewer Assigner
              </Link>
            )}

            {/* Review */}
            {canShow(ACL.review) && (
              <Link
                to="/review_page"
                className={`nav-link ${isActive('/review_page') ? 'active' : ''}`}
              >
                <FontAwesomeIcon icon={faClipboardCheck} />
                Review Papers
              </Link>
            )}

            {/* Upload Files */}
            {canShow(ACL.upload) && (
              <Link
                to="/upload_page"
                className={`nav-link ${isActive('/upload_page') ? 'active' : ''}`}
              >
                <FontAwesomeIcon icon={faUpload} />
                Upload Files
              </Link>
            )}

            {/* Analyze (hidden for now, but kept cleanly) */}
            {canShow(ACL.analyze) && (
              <>
                {/* 
                <Link
                  to="/analyze_page"
                  className={`nav-link ${isActive('/analyze_page') ? 'active' : ''}`}
                >
                  <FontAwesomeIcon icon={faSearch} />
                  Analyze Papers
                </Link>
                */}
              </>
            )}

            {/* Rubric Management */}
            {canShow(ACL.rubric) && (
              <Link
                to="/rubric_management"
                className={`nav-link ${isActive('/rubric_management') ? 'active' : ''}`}
              >
                <FontAwesomeIcon icon={faCog} />
                Rubric Management
              </Link>
            )}

            {/* Analytics */}
            {canShow(ACL.analytics) && (
              <Link
                to="/analytics"
                className={`nav-link ${isActive('/analytics') ? 'active' : ''}`}
              >
                <FontAwesomeIcon icon={faChartBar} />
                Analytics
              </Link>
            )}
          </nav>
        </div>

        {/* Logout pinned to the far right */}
        <div className="header-actions">
          <button
            className="logout-button"
            onClick={handleLogout}
            title="Logout"
            aria-label="Logout"
            disabled={isLoggingOut}
          >
            <FontAwesomeIcon icon={faSignOutAlt} className="logout-icon" />
            <span className="logout-text">Logout</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
